import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

interface OutputProps {
  children?: React.ReactNode;
  title: string;
  as?: 'div' | 'pre';
}

export function Output({ children, title, as = 'pre' }: OutputProps) {
  const containerRef = useRef<HTMLDivElement | HTMLPreElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [children]);

  function handleExpandClick() {
    setIsExpanded(!isExpanded);
  }

  const containerClassName = clsx(
    !isExpanded && 'relative h-40',
    isExpanded && 'h-screen w-full isolate fixed inset-0 z-50'
  );

  const contentClassName = clsx(
    'block h-full w-full rounded-md border-0 dark:border dark:border-gray-600 py-1.5 px-3 text-xs font-mono text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 leading-relaxed overflow-x-auto bg-white dark:bg-gray-800',
    isExpanded && 'overscroll-contain'
  );

  const Content = as === 'pre' ? 'pre' : 'div';

  return (
    <div>
      <span className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 mb-2">
        {title}
      </span>
      <div className={containerClassName}>
        <button
          type="button"
          className="absolute right-2 top-2 z-10 rounded p-1 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
          onClick={handleExpandClick}
        >
          {isExpanded ? (
            <ArrowsPointingInIcon className="w-4 h-4" />
          ) : (
            <ArrowsPointingOutIcon className="w-4 h-4" />
          )}
        </button>
        <Content
          className={contentClassName}
          ref={
            containerRef as React.RefObject<HTMLDivElement> &
              React.RefObject<HTMLPreElement>
          }
        >
          {children}
        </Content>
      </div>
    </div>
  );
}
